import type { Banner, MenuCategory, Product, ProductDisplaySettings } from '../types/menu';
import { imageCacheService } from './image-cache-service';
import { storage } from './storage';

const CACHE_KEY = 'bb-catalog-cache-v1';
const CACHE_VERSION = 1;

export type CachedCatalog = {
  version: number;
  savedAt: string;
  revision: string;
  products: Product[];
  categories: MenuCategory[];
  display: Record<string, ProductDisplaySettings>;
  banners: Banner[];
};

const empty: CachedCatalog | null = null;

function valid(value: CachedCatalog | null): value is CachedCatalog {
  return Boolean(value
    && value.version === CACHE_VERSION
    && Array.isArray(value.products)
    && value.products.length
    && Array.isArray(value.categories)
    && value.display && typeof value.display === 'object'
    && Array.isArray(value.banners));
}

const sourceProduct = (product: Product): Product => ({
  ...product,
  image: product.imageSource || product.image,
  modifier_groups: (product.modifier_groups ?? []).map((group) => ({
    ...group,
    items: group.items.map((item) => ({ ...item, image: item.imageSource || item.image })),
  })),
});
const hydratedProduct = (product: Product): Product => ({
  ...product,
  imageSource: product.imageSource || product.image,
  image: imageCacheService.resolve(product.imageSource || product.image),
  modifier_groups: (product.modifier_groups ?? []).map((group) => ({
    ...group,
    items: group.items.map((item) => {
      const source = item.imageSource || item.image || '/images/sauce-fallback.webp';
      return { ...item, imageSource: source, image: imageCacheService.resolve(source) };
    }),
  })),
});
const sourceBanner = (banner: Banner): Banner => ({ ...banner, image: banner.imageSource || banner.image });
const hydratedBanner = (banner: Banner): Banner => {
  const source = banner.imageSource || banner.image;
  return { ...banner, imageSource: source, image: imageCacheService.resolve(source) };
};

export const catalogCacheService = {
  read() {
    const cached = storage.get<CachedCatalog | null>(CACHE_KEY, empty);
    return valid(cached) ? {
      ...cached,
      products: cached.products.map(hydratedProduct),
      banners: cached.banners.map(hydratedBanner),
    } : null;
  },
  write(value: Omit<CachedCatalog, 'version' | 'savedAt'>) {
    return storage.set(CACHE_KEY, {
      ...value,
      products: value.products.map(sourceProduct),
      banners: value.banners.map(sourceBanner),
      version: CACHE_VERSION,
      savedAt: new Date().toISOString(),
    } satisfies CachedCatalog);
  },
};
